/**
 * Offers: the stage between the client choosing someone and them joining.
 *
 * The workbook this product replaced never separated the two — an offer that
 * was declined looked exactly like one that was never made. So the default
 * view here is what is still open, and a declined offer keeps its reason,
 * because "why do this client's offers keep getting turned down" is a question
 * nobody could previously answer.
 *
 * Accepting is the only action that creates a placement. That happens server
 * side; this screen surfaces the placement it produced so nobody goes looking
 * for it and records a second one by hand.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, HandCoins, Loader2, Plus, ThumbsDown, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import { money } from '../lib/money';
import { titleCaseName } from '../lib/titleCase';
import type {
  Offer,
  OfferAccepted,
  OfferStatus,
  OfferWithContext,
  Page,
  Submission,
} from '../types';

const STATUS_TONE: Record<OfferStatus, string> = {
  extended: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  withdrawn: 'bg-gray-200 text-gray-600',
};

const STATUS_LABEL: Record<OfferStatus, string> = {
  extended: 'Awaiting answer',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function ExtendForm({
  candidates,
  submitting,
  onSubmit,
}: {
  candidates: Submission[];
  submitting: boolean;
  onSubmit: (body: { submissionId: string; payload: Record<string, unknown> }) => void;
}) {
  const [submissionId, setSubmissionId] = useState('');
  const [offeredOn, setOfferedOn] = useState(today());
  const [offeredCtc, setOfferedCtc] = useState('');
  const [proposedJoiningDate, setProposedJoiningDate] = useState('');

  return (
    <form
      className="card space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submissionId) return;
        onSubmit({
          submissionId,
          payload: {
            offeredOn,
            offeredCtc: offeredCtc.trim() || null,
            proposedJoiningDate: proposedJoiningDate || null,
          },
        });
      }}
    >
      <h3 className="text-sm font-semibold text-gray-900">Extend An Offer</h3>
      <p className="text-xs text-gray-500">
        Only candidates the client has selected appear here, and only those
        without an offer already awaiting an answer.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-gray-600 sm:col-span-2">
          Candidate
          <select
            className="input mt-1"
            value={submissionId}
            onChange={(e) => setSubmissionId(e.target.value)}
            required
          >
            <option value="">Select a candidate…</option>
            {candidates.map((s) => (
              <option key={s.id} value={s.id}>
                {titleCaseName(s.candidateName) ?? s.candidateId}
                {s.clientName ? ` — ${s.clientName}` : ''}
                {s.appliedPosition ? ` (${s.appliedPosition})` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs text-gray-600">
          Offered On
          <input
            className="input mt-1"
            type="date"
            value={offeredOn}
            onChange={(e) => setOfferedOn(e.target.value)}
          />
        </label>

        <label className="text-xs text-gray-600">
          Annual CTC
          <input
            spellCheck={false}
            className="input mt-1"
            inputMode="decimal"
            value={offeredCtc}
            onChange={(e) => setOfferedCtc(e.target.value)}
            placeholder="850000"
          />
        </label>

        <label className="text-xs text-gray-600">
          Proposed Joining Date
          <input
            className="input mt-1"
            type="date"
            value={proposedJoiningDate}
            onChange={(e) => setProposedJoiningDate(e.target.value)}
          />
        </label>
      </div>

      {candidates.length === 0 && (
        <p className="text-xs text-gray-500">
          Nobody is currently selected and waiting on an offer.
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className="btn-primary"
          disabled={!submissionId || submitting}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Extend offer
        </button>
      </div>
    </form>
  );
}

function RefuseDialog({
  offer,
  action,
  submitting,
  onCancel,
  onConfirm,
}: {
  offer: OfferWithContext;
  action: 'decline' | 'withdraw';
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (remarks: string) => void;
}) {
  const [remarks, setRemarks] = useState('');
  const declining = action === 'decline';

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <div className="card w-full max-w-md space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {declining ? 'Candidate declined the offer' : 'Withdraw the offer'}
        </h3>
        <p className="text-xs text-gray-500">
          {offer.candidateName ?? 'This candidate'}
          {offer.clientName ? ` · ${offer.clientName}` : ''}
        </p>
        <label className="block text-xs text-gray-600">
          {declining ? 'Why did they decline?' : 'Why is it being withdrawn?'}
          <textarea
            spellCheck={true}
            className="input mt-1"
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={
              declining ? 'Took a counter-offer' : 'Client put the role on hold'
            }
          />
        </label>
        <p className="text-[11px] text-gray-400">
          Worth recording — this is the number that answers why offers are
          being lost.
        </p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => onConfirm(remarks)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {declining ? 'Record decline' : 'Withdraw'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Accepting an offer, and the two columns the placement would otherwise lack.
 *
 * Acceptance is the only thing that creates a placement, and it never set
 * `location` or `date_of_interview` — so every placement made through this flow
 * carried two permanently blank tracker columns, with no edit route that could
 * fill them in afterwards.
 *
 * Both are optional. Left blank the server derives them from the posting's
 * location and the submission's own interview record, so the usual case needs
 * nothing typed here at all; the boxes are for the rows that know neither,
 * which is most of what came out of the workbook.
 */
function AcceptDialog({
  offer,
  submitting,
  onCancel,
  onConfirm,
}: {
  offer: OfferWithContext;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (payload: { location: string; dateOfInterview: string }) => void;
}) {
  const [location, setLocation] = useState('');
  const [dateOfInterview, setDateOfInterview] = useState('');

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card w-full max-w-md space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm({ location, dateOfInterview });
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          Candidate Accepted the Offer
        </h3>
        <p className="text-xs text-gray-500">
          {offer.candidateName ?? 'This candidate'}
          {offer.clientName ? ` · ${offer.clientName}` : ''}
        </p>
        <p className="text-xs text-gray-500">
          This creates the placement. Two tracker columns are worth filling in
          now, while somebody knows them.
        </p>

        <label className="block text-xs text-gray-600">
          Location
          <input
            spellCheck={false}
            className="input mt-1"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={120}
            placeholder="Taken from the job posting if left blank"
          />
        </label>

        <label className="block text-xs text-gray-600">
          Date of Interview
          <input
            className="input mt-1"
            type="date"
            value={dateOfInterview}
            onChange={(e) => setDateOfInterview(e.target.value)}
          />
          <span className="mt-0.5 block text-[11px] text-gray-400">
            Taken from the last interview on record if left blank
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Record acceptance
          </button>
        </div>
      </form>
    </div>
  );
}

export default function OffersPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [openOnly, setOpenOnly] = useState(true);
  /**
   * Narrow to the offers on this person's own submissions.
   *
   * An offer has no recruiter of its own; it belongs to whoever made the
   * submission under it, which is the same join the server already walks to
   * decide who may see it at all.
   */
  const [mineOnly, setMineOnly] = useState(false);
  const [refusing, setRefusing] = useState<{
    offer: OfferWithContext;
    action: 'decline' | 'withdraw';
  } | null>(null);
  const [accepting, setAccepting] = useState<OfferWithContext | null>(null);

  const offers = useQuery<Page<OfferWithContext>>({
    queryKey: ['offers', openOnly, mineOnly],
    queryFn: async () =>
      (await client.get('/api/v1/offers', {
        params: {
          limit: 200,
          ...(openOnly ? { openOnly: true } : {}),
          ...(mineOnly ? { mineOnly: true } : {}),
        },
      })).data,
  });

  /** Every open offer, regardless of the list filter — used to keep the
   *  picker free of submissions the server would refuse anyway. */
  const openOffers = useQuery<Page<Offer>>({
    queryKey: ['offers', 'open'],
    queryFn: async () =>
      (await client.get('/api/v1/offers', { params: { limit: 200, openOnly: true } })).data,
    enabled: showForm,
  });

  const selected = useQuery<Page<Submission>>({
    queryKey: ['submissions', 'selected'],
    queryFn: async () =>
      (await client.get('/api/v1/submissions', {
        params: { limit: 200, status: 'selected' },
      })).data,
    enabled: showForm,
  });

  const offerable = useMemo(() => {
    const taken = new Set((openOffers.data?.items ?? []).map((o) => o.submissionId));
    return (selected.data?.items ?? []).filter((s) => !taken.has(s.id));
  }, [selected.data, openOffers.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['offers'] });
    queryClient.invalidateQueries({ queryKey: ['submissions'] });
    // Accepting moves the submission on, which changes a posting's funnel.
    queryClient.invalidateQueries({ queryKey: ['job-openings'] });
  };

  const extend = useMutation({
    mutationFn: async ({
      submissionId,
      payload,
    }: {
      submissionId: string;
      payload: Record<string, unknown>;
    }) =>
      (await client.post(`/api/v1/submissions/${submissionId}/offers`, payload)).data,
    onSuccess: () => {
      invalidate();
      setShowForm(false);
      toast.success('Offer extended');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const accept = useMutation({
    mutationFn: async ({
      offerId,
      payload,
    }: {
      offerId: string;
      payload: Record<string, unknown>;
    }) =>
      (await client.post(`/api/v1/offers/${offerId}/accept`, payload))
        .data as OfferAccepted,
    onSuccess: (data) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      setAccepting(null);
      // Naming the placement is the point: otherwise somebody goes looking
      // for it and records a second one by hand.
      toast.success(`Offer accepted — placement ${data.placementId} created`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const refuse = useMutation({
    mutationFn: async ({
      offerId,
      action,
      remarks,
    }: {
      offerId: string;
      action: 'decline' | 'withdraw';
      remarks: string;
    }) =>
      (await client.post(`/api/v1/offers/${offerId}/${action}`, {
        remarks: remarks.trim() || null,
      })).data,
    onSuccess: (_data, variables) => {
      invalidate();
      setRefusing(null);
      toast.success(variables.action === 'decline' ? 'Decline recorded' : 'Offer withdrawn');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = offers.data?.items ?? [];

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Offers</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              Between selection and joining. Accepting one creates the
              placement that billing later invoices against — declining and
              withdrawing create nothing, but both keep their reason.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'Extend offer'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            Awaiting an answer only
          </label>

          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
            Only mine
          </label>
        </div>
      </section>

      {showForm && (
        <ExtendForm
          candidates={offerable}
          submitting={extend.isPending}
          onSubmit={extend.mutate}
        />
      )}

      {offers.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : offers.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load offers.</p>
          <button type="button" className="btn-ghost" onClick={() => void offers.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card flex items-center gap-3 text-sm text-gray-500">
          <HandCoins className="h-4 w-4 text-gray-400" />
          {openOnly
            ? 'No offers awaiting an answer.'
            : 'No offers have been extended yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((offer) => (
            <section key={offer.id} className="card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {titleCaseName(offer.candidateName) ?? 'Unknown candidate'}
                    </h3>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                        STATUS_TONE[offer.status]
                      }`}
                    >
                      {STATUS_LABEL[offer.status]}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {offer.clientName ?? 'Unknown client'}
                    {offer.appliedPosition ? ` · ${offer.appliedPosition}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Offered {offer.offeredOn}
                    {offer.offeredCtc ? ` · ${money(offer.offeredCtc)} a year` : ''}
                    {offer.proposedJoiningDate
                      ? ` · joining ${offer.proposedJoiningDate}`
                      : ''}
                  </p>
                  {offer.remarks && (
                    <p className="mt-2 rounded bg-gray-50 px-2 py-1 text-xs text-gray-600">
                      {offer.remarks}
                    </p>
                  )}
                </div>

                {offer.status === 'extended' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-primary text-xs"
                      disabled={accept.isPending}
                      onClick={() => setAccepting(offer)}
                    >
                      <BadgeCheck className="h-4 w-4" />
                      Accepted
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => setRefusing({ offer, action: 'decline' })}
                    >
                      <ThumbsDown className="h-4 w-4" />
                      Declined
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => setRefusing({ offer, action: 'withdraw' })}
                    >
                      <Undo2 className="h-4 w-4" />
                      Withdraw
                    </button>
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      {accepting && (
        <AcceptDialog
          key={accepting.id}
          offer={accepting}
          submitting={accept.isPending}
          onCancel={() => setAccepting(null)}
          onConfirm={({ location, dateOfInterview }) =>
            accept.mutate({
              offerId: accepting.id,
              // Only sent when filled in. An empty string would be a value the
              // server has to interpret; omitting the key lets it derive.
              payload: {
                ...(location.trim() ? { location: location.trim() } : {}),
                ...(dateOfInterview ? { dateOfInterview } : {}),
              },
            })
          }
        />
      )}

      {refusing && (
        <RefuseDialog
          offer={refusing.offer}
          action={refusing.action}
          submitting={refuse.isPending}
          onCancel={() => setRefusing(null)}
          onConfirm={(remarks) =>
            refuse.mutate({
              offerId: refusing.offer.id,
              action: refusing.action,
              remarks,
            })
          }
        />
      )}
    </div>
  );
}
